import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    TableCell,
    TableRow,
    IconButton,
    Tooltip,
    Alert,
    Snackbar,
    Switch,
    Typography,
    Popover,
    Divider,
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
} from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { AppTable } from '../../../shared/components/AppTable';
import { Loader } from '../../../shared/components/Loader';
import { usePermission } from '../../../shared/contexts/PermissionContext';
import { getDeliveryCharges, toggleDeliveryCharge, deleteDeliveryCharge } from '../../../shared/services/deliveryChargeService';
import { DeliveryCharge as DeliveryChargeType } from '../../../shared/types/deliveryCharge';

export const DeliveryCharge: React.FC = () => {
    const navigate = useNavigate();
    const { hasPermission, isSuperAdmin } = usePermission();
    const [charges, setCharges] = useState<DeliveryChargeType[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [deleteAnchorEl, setDeleteAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [selectedChargeId, setSelectedChargeId] = useState<string | null>(null);

    useEffect(() => {
        loadCharges();
    }, []);

    const loadCharges = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await getDeliveryCharges();

            if (response.success && response.data) {
                // Robustly handle different response formats
                const data = response.data;
                if (Array.isArray(data)) {
                    setCharges(data);
                } else if (data && typeof data === 'object') {
                    const items = (data as any).items || (data as any).data || [];
                    setCharges(Array.isArray(items) ? items : []);
                } else {
                    setCharges([]);
                }
            } else {
                setCharges([]);
            }
        } catch (err: any) {
            console.error('Error loading delivery charges:', err);
            setError(err.response?.data?.message || 'Failed to load delivery charges');
            setCharges([]);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = async (id: string) => {
        try {
            const response = await toggleDeliveryCharge(id);
            if (response.success) {
                setSuccess('Status updated successfully');
                setCharges(prev => prev.map(c => c.id === id ? { ...c, isActive: !c.isActive } : c));
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to update status');
        }
    };

    const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
        setDeleteAnchorEl(event.currentTarget);
        setSelectedChargeId(id);
    };

    const handleDeleteClose = () => {
        setDeleteAnchorEl(null);
        setSelectedChargeId(null);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedChargeId) return;

        try {
            const response = await deleteDeliveryCharge(selectedChargeId);
            if (response.success) {
                setSuccess('Delivery charge deleted successfully');
                setCharges(prev => prev.filter(c => c.id !== selectedChargeId));
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to delete delivery charge');
        } finally {
            handleDeleteClose();
        }
    };

    const handleCloseSnackbar = () => {
        setError('');
        setSuccess('');
    };

    return (
        <div className="flex flex-col flex-auto min-w-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                <div className="flex-1 min-w-0">
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                        Delivery Charges
                    </h2>
                </div>
                {(isSuperAdmin() || hasPermission('deliveryCharge.add')) && (
                    <div className="flex items-center gap-4 mt-4 sm:mt-0 flex-wrap">
                        <AppButton
                            variant="primary"
                            startIcon={<AddIcon />}
                            onClick={() => navigate('/delivery-charge/add')}
                        >
                            Add Delivery Charge
                        </AppButton>
                    </div>
                )}
            </div>

            {/* Content Area */}
            <div className="flex flex-auto p-6 sm:p-10">
                <div className="flex flex-col flex-auto min-w-0 w-full">
                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : (!Array.isArray(charges) || charges.length === 0) ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Alert severity="info">No delivery charges found.</Alert>
                        </Box>
                    ) : (
                        <AppTable
                            head={
                                <TableRow className="bg-gray-100">
                                    <TableCell className="!font-semibold !text-gray-700">Min Order Amount (₹)</TableCell>
                                    <TableCell className="!font-semibold !text-gray-700">Delivery Charge (₹)</TableCell>
                                    <TableCell className="!font-semibold !text-gray-700">Status</TableCell>
                                    <TableCell className="!font-semibold !text-gray-700" align="right">Actions</TableCell>
                                </TableRow>
                            }
                            isEmpty={!Array.isArray(charges) || charges.length === 0}
                            emptyColSpan={4}
                        >
                            {Array.isArray(charges) && charges.map((charge) => (
                                <TableRow key={charge.id || charge._id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                            ₹{(charge.minOrderAmount || 0).toLocaleString()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="primary" fontWeight="bold">
                                            ₹{(charge.charge || 0).toLocaleString()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Switch
                                            checked={charge.isActive}
                                            onChange={() => (charge.id || charge._id) && handleToggleStatus((charge.id || charge._id)!)}
                                            disabled={!isSuperAdmin() && !hasPermission('deliveryCharge.update')}
                                            color="primary"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <div className="flex justify-end gap-2">
                                            {(isSuperAdmin() || hasPermission('deliveryCharge.update')) && (
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => navigate(`/delivery-charge/edit/${charge.id || charge._id}`)}
                                                        sx={{ color: '#0EA5E9' }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {(isSuperAdmin() || hasPermission('deliveryCharge.delete')) && (
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => (charge.id || charge._id) && handleDeleteClick(e, (charge.id || charge._id)!)}
                                                        sx={{ color: '#EF4444' }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </AppTable>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Popover */}
            <Popover
                open={Boolean(deleteAnchorEl)}
                anchorEl={deleteAnchorEl}
                onClose={handleDeleteClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        p: 0,
                        mt: 1.5,
                        width: 250,
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                    },
                }}
            >
                <div className="p-4 bg-white">
                    <Typography className="font-semibold text-gray-900" sx={{ mb: 1 }}>
                        Are you sure?
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        This delivery charge will be permanently deleted.
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <div className="flex justify-end gap-2">
                        <AppButton
                            variant="secondary"
                            size="small"
                            onClick={handleDeleteClose}
                        >
                            Cancel
                        </AppButton>
                        <AppButton
                            variant="danger"
                            size="small"
                            onClick={handleDeleteConfirm}
                        >
                            Delete
                        </AppButton>
                    </div>
                </div>
            </Popover>

            {/* Snackbar */}
            <Snackbar
                open={!!error || !!success}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {error ? (
                    <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                ) : (
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                        {success}
                    </Alert>
                )}
            </Snackbar>
        </div>
    );
};
